// Exibição de leads frios: rótulo/tom de status, rótulo de tipo de coluna e
// formatação pt-BR de valores de `prospects.data`. Puro, sem React.
import type { StatusMeta, StatusTone } from "./statusTone";
import type { ProspectFieldDef, ProspectFieldType, ProspectStatus } from "./prospectFields";

export const PROSPECT_STATUS_META: Record<ProspectStatus, StatusMeta> = {
  new: { label: "Novo", tone: "primary" },
  researching: { label: "Pesquisando", tone: "info" },
  qualified: { label: "Qualificado", tone: "notice" },
  promoted: { label: "No funil", tone: "ok" },
  discarded: { label: "Descartado", tone: "neutral" },
};

/** Ordem dos chips de filtro e do menu de status (promovido só via "Mover para o funil"). */
export const PROSPECT_STATUS_ORDER: ProspectStatus[] = [
  "new",
  "researching",
  "qualified",
  "promoted",
  "discarded",
];

export const EDITABLE_PROSPECT_STATUSES: ProspectStatus[] = [
  "new",
  "researching",
  "qualified",
  "discarded",
];

export const FIELD_TYPE_META: Record<ProspectFieldType, { label: string; icon: string }> = {
  text: { label: "Texto", icon: "Aa" },
  long_text: { label: "Texto longo", icon: "¶" },
  number: { label: "Número", icon: "#" },
  currency: { label: "Moeda (R$)", icon: "R$" },
  select: { label: "Seleção", icon: "◉" },
  multi_select: { label: "Múltipla seleção", icon: "☰" },
  rating: { label: "Avaliação (0–5)", icon: "★" },
  boolean: { label: "Sim / não", icon: "✓" },
  date: { label: "Data", icon: "▦" },
  url: { label: "Link", icon: "↗" },
  email: { label: "E-mail", icon: "@" },
  phone: { label: "Telefone", icon: "☎" },
};

const numberFmt = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
const currencyFmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

/** "aaaa-mm-dd" → "dd/mm/aaaa" sem passar por fuso horário. */
export function formatIsoDate(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
}

/** Host curto para links ("https://www.drfavero.com/x" → "drfavero.com/x"). */
export function shortUrl(value: string): string {
  return value
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "");
}

/** Valor de coluna personalizada → texto de exibição ("" quando vazio). */
export function formatProspectValue(
  field: Pick<ProspectFieldDef, "type" | "options">,
  value: unknown,
): string {
  if (value === null || value === undefined || value === "") return "";
  switch (field.type) {
    case "number":
      return typeof value === "number" ? numberFmt.format(value) : String(value);
    case "currency":
      return typeof value === "number" ? currencyFmt.format(value) : String(value);
    case "rating": {
      const n = Math.max(0, Math.min(5, Math.round(Number(value))));
      return Number.isFinite(n) ? `${"★".repeat(n)}${"☆".repeat(5 - n)}` : String(value);
    }
    case "boolean":
      return value === true ? "Sim" : value === false ? "Não" : String(value);
    case "date":
      return typeof value === "string" ? formatIsoDate(value) : String(value);
    case "url":
      return typeof value === "string" ? shortUrl(value) : String(value);
    case "select":
      return optionLabel(field.options, String(value));
    case "multi_select":
      return Array.isArray(value)
        ? value.map((v) => optionLabel(field.options, String(v))).join(", ")
        : String(value);
    default:
      return typeof value === "string" ? value : JSON.stringify(value);
  }
}

function optionLabel(options: ProspectFieldDef["options"], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

export function optionTone(field: Pick<ProspectFieldDef, "options">, value: string): StatusTone {
  return field.options.find((o) => o.value === value)?.tone ?? "neutral";
}

/** Valor atual de coluna → texto inicial do editor (pt-BR para número). */
export function editorText(type: ProspectFieldType, value: unknown): string {
  if (value === null || value === undefined) return "";
  if ((type === "number" || type === "currency") && typeof value === "number") {
    return String(value).replace(".", ",");
  }
  if (type === "date" && typeof value === "string") return value.slice(0, 10);
  return typeof value === "string" ? value : String(value);
}
